import axios from 'axios';

const config = window.__RUNTIME_CONFIG__ || {};
const API_URL = config.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

// Every AI endpoint below throws the backend's own error message when present,
// falling back to a generic one otherwise — factored out so a future endpoint
// doesn't have to copy the same try/catch body again.
const unwrapAiError = (error, fallbackMessage = 'Network error. Please check your connection and try again.') => {
  if (error.response?.data?.message) throw new Error(error.response.data.message);
  throw new Error(fallbackMessage);
};

export const getConfig = async (customerId) => {
  try {
    const response = await axios.get(`${API_URL}/ai/config`, customerId ? { params: { customerId } } : undefined);
    return response.data.data;
  } catch (error) {
    unwrapAiError(error);
  }
};

export const saveConfig = async (payload) => {
  try {
    const response = await axios.put(`${API_URL}/ai/config`, payload);
    return response.data;
  } catch (error) {
    unwrapAiError(error);
  }
};

export const patchConfig = async (payload) => {
  try {
    const response = await axios.patch(`${API_URL}/ai/config`, payload);
    return response.data;
  } catch (error) {
    unwrapAiError(error);
  }
};

export const chatbot = async (message, customerId) => {
  try {
    const response = await axios.post(
      `${API_URL}/ai/chatbot`,
      { message, customerId },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (response?.data?.data?.message) {
      return response.data.data.message;
    }

    return "Sorry, I couldn't understand that.";
  } catch (error) {
    console.error('Chatbot API error:', error);
    unwrapAiError(error);
  }
};

export const testModel = async (provider, model, apiKey, baseUrl, temperature, maxTokens, customHeaders) => {
  try {
    const payload = { provider, model, apiKey, baseUrl, temperature, maxTokens };
    if (customHeaders) payload.customHeaders = customHeaders;
    const response = await axios.post(`${API_URL}/ai/test`, payload);
    return response.data.data;
  } catch (error) {
    unwrapAiError(error);
  }
};

export const testModelStored = async (provider, model, customerId) => {
  try {
    const response = await axios.post(`${API_URL}/ai/test-stored`, { provider, model, customerId });
    return response.data.data;
  } catch (error) {
    unwrapAiError(error);
  }
};

export const generateInsights = async (data, prompt, customerId) => {
  try {
    const response = await axios.post(
      `${API_URL}/ai/insights`,
      { data, prompt, customerId },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (response?.data?.data?.insight) {
      return response.data.data.insight;
    }

    return 'Unable to generate insights from the provided data.';
  } catch (error) {
    console.error('Insights API error:', error);
    unwrapAiError(error, 'Error generating insights. Please try again.');
  }
};

export const generateTemplate = async (params) => {
  try {
    const response = await axios.post(`${API_URL}/ai/generate-template`, params);
    return response.data.data;
  } catch (error) {
    unwrapAiError(error);
  }
};

export const generateLandingPage = async (params) => {
  try {
    const response = await axios.post(`${API_URL}/ai/generate-landing-page`, params);
    return response.data.data; // { content, pageType }
  } catch (error) {
    unwrapAiError(error);
  }
};

export const improveTemplate = async (params) => {
  try {
    const response = await axios.post(`${API_URL}/ai/improve-template`, params);
    return response.data.data;
  } catch (error) {
    unwrapAiError(error);
  }
};

export default {
  getConfig,
  saveConfig,
  patchConfig,
  chatbot,
  testModel,
  testModelStored,
  generateInsights,
  generateTemplate,
  generateLandingPage,
  improveTemplate,
};
